import {
  Tag, Info, CheckCircle2, Ruler, Box, Globe,
} from 'lucide-react';
import { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { TabsContent } from '../ui/tabs';
import { CountryFlag } from './CountryFlag';
import { FormSection, TagInput } from './FormInputs';
import { CategorySelector } from './CategorySelector';
import { UNITS, TVA_RATES, COUNTRIES, formatPrice } from './constants';

const readCustom = (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } };
const saveCustom = (key, list) => { try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* plein */ } };

// Sélecteur TVA avec création directe d'un taux personnalisé
const TvaSelect = ({ formData, handleChange }) => {
  const [customs, setCustoms] = useState(() => readCustom('kdm_custom_tva'));
  const all = [...TVA_RATES, ...customs.filter((c) => !TVA_RATES.some((t) => t.value === c.value))];
  const current = parseFloat(formData.tva_rate);
  if (!Number.isNaN(current) && !all.some((t) => t.value === current)) {
    all.push({ value: current, label: `${current}% (Personnalisé)` });
  }
  const onChange = (v) => {
    if (v === '__new__') {
      const input = window.prompt('Nouveau taux de TVA en % (ex : 8.5)');
      if (input === null) return;
      const rate = parseFloat(String(input).replace(',', '.'));
      if (Number.isNaN(rate) || rate < 0 || rate > 100) { window.alert('Taux invalide (0 à 100).'); return; }
      const next = [...customs.filter((c) => c.value !== rate), { value: rate, label: `${rate}% (Personnalisé)` }];
      setCustoms(next);
      saveCustom('kdm_custom_tva', next);
      handleChange('tva_rate', rate);
      return;
    }
    handleChange('tva_rate', parseFloat(v));
  };
  return (
    <Select value={String(formData.tva_rate)} onValueChange={onChange}>
      <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10" data-testid="product-tva-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {all.sort((a, b) => a.value - b.value).map((t) => (
          <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
        ))}
        <SelectItem value="__new__" data-testid="product-tva-create">＋ Créer un taux de TVA…</SelectItem>
      </SelectContent>
    </Select>
  );
};

// Sélecteur unité de vente avec création directe d'une unité personnalisée
const UnitSelect = ({ formData, handleChange }) => {
  const [customs, setCustoms] = useState(() => readCustom('kdm_custom_units'));
  const all = [...UNITS, ...customs.filter((c) => !UNITS.some((u) => u.value === c.value))];
  if (formData.unit_type && !all.some((u) => u.value === formData.unit_type)) {
    all.push({ value: formData.unit_type, label: formData.unit_type });
  }
  const onChange = (v) => {
    if (v === '__new__') {
      const input = window.prompt("Nouvelle unité de vente (ex : Botte, Fût 30L, Rouleau)");
      if (input === null) return;
      const label = String(input).trim();
      if (!label) return;
      const next = [...customs.filter((c) => c.value !== label), { value: label, label }];
      setCustoms(next);
      saveCustom('kdm_custom_units', next);
      handleChange('unit_type', label);
      return;
    }
    handleChange('unit_type', v);
  };
  return (
    <Select value={formData.unit_type} onValueChange={onChange}>
      <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10" data-testid="product-unit-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {all.map((u) => (
          <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
        ))}
        <SelectItem value="__new__" data-testid="product-unit-create">＋ Créer une unité de vente…</SelectItem>
      </SelectContent>
    </Select>
  );
};

export const BasicTab = ({ formData, handleChange }) => (
            <TabsContent value="basic" className="space-y-4">
              <FormSection title="Identification" icon={Tag}>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/70 text-xs">SKU *</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => handleChange('sku', e.target.value)}
                      placeholder="REF-001"
                      className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Code EAN-13</Label>
                    <Input
                      value={formData.ean}
                      onChange={(e) => handleChange('ean', e.target.value)}
                      placeholder="3760012345678"
                      className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Réf. fabricant</Label>
                    <Input
                      value={formData.manufacturer_ref}
                      onChange={(e) => handleChange('manufacturer_ref', e.target.value)}
                      className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection title="Informations produit" icon={Info}>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/70 text-xs">Nom du produit *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Nom du produit"
                      className="mt-1 bg-white/[0.04] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Description courte</Label>
                    <Input
                      value={formData.short_description}
                      onChange={(e) => handleChange('short_description', e.target.value)}
                      placeholder="Description courte (max 200 car.)"
                      maxLength={200}
                      className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Description complète</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Description détaillée du produit..."
                      rows={3}
                      className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                    />
                  </div>
                  <CategorySelector formData={formData} handleChange={handleChange} />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 text-xs">Marque</Label>
                      <Input
                        value={formData.brand}
                        onChange={(e) => handleChange('brand', e.target.value)}
                        className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Fabricant</Label>
                      <Input
                        value={formData.manufacturer}
                        onChange={(e) => handleChange('manufacturer', e.target.value)}
                        className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Tags</Label>
                    <TagInput
                      value={formData.tags}
                      onChange={(v) => handleChange('tags', v)}
                      placeholder="Ajouter un tag..."
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection title="Statut & Options" icon={CheckCircle2}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-xs">Statut</Label>
                    <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                      <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Brouillon</SelectItem>
                        <SelectItem value="pending_approval">En attente</SelectItem>
                        <SelectItem value="approved">Approuvé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.is_active} onCheckedChange={(v) => handleChange('is_active', v)} />
                    <span className="text-sm text-white/80">Actif</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.is_new} onCheckedChange={(v) => handleChange('is_new', v)} />
                    <span className="text-sm text-white/80">Nouveauté</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.is_featured} onCheckedChange={(v) => handleChange('is_featured', v)} />
                    <span className="text-sm text-white/80">Mis en avant</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="product-image-lot3-toggle">
                    <Checkbox checked={!!formData.image_lot3} onCheckedChange={(v) => handleChange('image_lot3', v)} />
                    <span className="text-sm text-white/80">Affichage image ×3 (lot de 3)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="product-visitor-visible-toggle">
                    <Checkbox checked={!!formData.visitor_visible} onCheckedChange={(v) => handleChange('visitor_visible', v)} />
                    <span className="text-sm text-white/80">Visible par les visiteurs (vitrine)</span>
                  </label>
                </div>
                <div className="mt-3">
                  <Label className="text-white/80">Image principale du produit</Label>
                  <div className="mt-1 flex items-center gap-2">
                    {formData.image_url && (
                      <img src={formData.image_url.startsWith('http') ? formData.image_url : `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`}
                        alt="" className="w-12 h-12 object-cover rounded-lg border border-white/15" data-testid="main-image-preview" />
                    )}
                    <Input
                      data-testid="product-main-image-url"
                      placeholder="https://…/photo.jpg ou téléversez →"
                      value={formData.image_url || ''}
                      onChange={(e) => handleChange('image_url', e.target.value)}
                      className="flex-1 bg-white/[0.04] border-white/10 text-white"
                    />
                    <input type="file" id="main-image-upload" accept="image/png,image/jpeg,image/webp" className="hidden"
                      data-testid="product-main-image-upload-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('file', file);
                        try {
                          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/admin/upload-image`, {
                            method: 'POST', credentials: 'include', body: fd,
                          });
                          const d = await r.json();
                          if (!r.ok) throw new Error(d.detail || 'Erreur upload');
                          handleChange('image_url', d.url);
                        } catch (err) { window.alert(err.message); }
                        e.target.value = '';
                      }} />
                    <label htmlFor="main-image-upload" data-testid="product-main-image-upload-btn"
                      className="inline-flex items-center px-3 h-10 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap bg-[#D9B35A]/15 text-[#E9CF8E] border border-[#D9B35A]/40 hover:bg-[#D9B35A]/25 transition-colors">
                      📤 Téléverser
                    </label>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-white/80">Galerie photos (une URL par ligne, défilement sur la fiche)</Label>
                  <Textarea
                    data-testid="product-gallery-input"
                    rows={3}
                    placeholder={'https://…/photo1.jpg\nhttps://…/photo2.jpg'}
                    value={formData.gallery_text !== undefined ? formData.gallery_text : (formData.images || []).map((s) => (typeof s === 'string' ? s : s?.url || '')).filter(Boolean).join('\n')}
                    onChange={(e) => handleChange('gallery_text', e.target.value)}
                    className="mt-1 bg-white/[0.04] border-white/10 text-white"
                  />
                  <div className="mt-2">
                    <input
                      type="file"
                      id="gallery-upload-input"
                      data-testid="product-gallery-upload-input"
                      accept="image/png,image/jpeg,image/webp"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;
                        const current = formData.gallery_text !== undefined
                          ? formData.gallery_text
                          : (formData.images || []).map((s) => (typeof s === 'string' ? s : s?.url || '')).filter(Boolean).join('\n');
                        let text = current;
                        for (const f of files) {
                          const fd = new FormData();
                          fd.append('file', f);
                          try {
                            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/admin/upload-image`, {
                              method: 'POST', credentials: 'include', body: fd,
                            });
                            const d = await r.json();
                            if (!r.ok) throw new Error(d.detail || 'Erreur upload');
                            text = text ? `${text}\n${d.url}` : d.url;
                          } catch (err) {
                            window.alert(`${f.name} : ${err.message}`);
                          }
                        }
                        handleChange('gallery_text', text);
                        e.target.value = '';
                      }}
                    />
                    <label htmlFor="gallery-upload-input"
                      data-testid="product-gallery-upload-btn"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer bg-[#D9B35A]/15 text-[#E9CF8E] border border-[#D9B35A]/40 hover:bg-[#D9B35A]/25 transition-colors">
                      📤 Téléverser des photos (PNG/JPG/WEBP, max 4 Mo)
                    </label>
                  </div>
                </div>
              </FormSection>
            </TabsContent>
);

export const PricingTab = ({ formData, handleChange }) => (
            <TabsContent value="pricing" className="space-y-4">
              <FormSection title="Tarification" icon={Tag}>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/70 text-xs">Prix HT (centimes) *</Label>
                    <Input
                      type="number"
                      value={formData.price_ht_cents}
                      onChange={(e) => handleChange('price_ht_cents', e.target.value)}
                      placeholder="1000 = 10,00€"
                      className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                    />
                    <p className="text-xs text-white/40 mt-1">= {formatPrice(formData.price_ht_cents)}</p>
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Taux TVA</Label>
                    <TvaSelect formData={formData} handleChange={handleChange} />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Unité de vente</Label>
                    <UnitSelect formData={formData} handleChange={handleChange} />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Libellé unité</Label>
                  <Input
                    value={formData.unit_label}
                    onChange={(e) => handleChange('unit_label', e.target.value)}
                    placeholder="Ex: sac de 5kg, carton de 12..."
                    className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm"
                  />
                </div>
              </FormSection>

              <FormSection title="Dimensions & Poids" icon={Ruler}>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-white/70 text-xs">Longueur (cm)</Label>
                    <Input type="number" value={formData.length_cm} onChange={(e) => handleChange('length_cm', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Largeur (cm)</Label>
                    <Input type="number" value={formData.width_cm} onChange={(e) => handleChange('width_cm', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Hauteur (cm)</Label>
                    <Input type="number" value={formData.height_cm} onChange={(e) => handleChange('height_cm', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Volume (L)</Label>
                    <Input type="number" value={formData.volume_l} onChange={(e) => handleChange('volume_l', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-white/70 text-xs">Poids net (kg)</Label>
                    <Input type="number" step="0.01" value={formData.net_weight_kg} onChange={(e) => handleChange('net_weight_kg', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Poids brut (kg)</Label>
                    <Input type="number" step="0.01" value={formData.gross_weight_kg} onChange={(e) => handleChange('gross_weight_kg', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                </div>
              </FormSection>

              <FormSection title="Conditionnement" icon={Box}>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/70 text-xs">Unités/pack</Label>
                    <Input type="number" value={formData.unit_per_pack} onChange={(e) => handleChange('unit_per_pack', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Packs/carton</Label>
                    <Input type="number" value={formData.pack_per_carton} onChange={(e) => handleChange('pack_per_carton', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Cartons/palette</Label>
                    <Input type="number" value={formData.carton_per_pallet} onChange={(e) => handleChange('carton_per_pallet', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-white/70 text-xs">Type d'emballage</Label>
                    <Input value={formData.packaging_type} onChange={(e) => handleChange('packaging_type', e.target.value)} placeholder="Bouteille, sac, carton..." className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={formData.is_recyclable} onCheckedChange={(v) => handleChange('is_recyclable', v)} />
                      <span className="text-sm text-white/80">Emballage recyclable</span>
                    </label>
                  </div>
                </div>
              </FormSection>

              <FormSection title="Origine" icon={Globe}>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/70 text-xs">Pays d'origine</Label>
                    <Select value={formData.country_code} onValueChange={(v) => handleChange('country_code', v)}>
                      <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10">
                        <SelectValue>
                          {formData.country_code && (
                            <div className="flex items-center gap-2">
                              <CountryFlag countryCode={formData.country_code} size={20} />
                              <span>{COUNTRIES.find(c => c.code === formData.country_code)?.name}</span>
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            <div className="flex items-center gap-2">
                              <CountryFlag countryCode={c.code} size={20} />
                              <span>{c.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Région</Label>
                    <Input value={formData.region} onChange={(e) => handleChange('region', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Producteur</Label>
                    <Input value={formData.producer_name} onChange={(e) => handleChange('producer_name', e.target.value)} className="mt-1 bg-white/[0.04] border-white/10 text-white text-sm" />
                  </div>
                </div>
              </FormSection>
            </TabsContent>
);
